// Executive Challenge backend — implementation selector.
//
// Two implementations of one interface: the Phase 1 local stub (./local.ts,
// fully offline) and the real Supabase service (./supabase.ts). Which one runs
// is purely a config question, so callers import from here and never know the
// difference. With config.ts empty — the default — this module re-exports the
// stub, byte-identical to pre-backend behavior.

import {
	SUPABASE_ANON_KEY,
	SUPABASE_AUTH_MODE,
	SUPABASE_URL,
} from "./config.ts";
import * as local from "./local.ts";
import * as supabase from "./supabase.ts";
import type { ChallengeBackend } from "./types.ts";

export { ChallengeCodeError } from "./types.ts";
export type {
	ChallengeBackend,
	ChallengeEntry,
	ChallengeRow,
} from "./types.ts";

// Both values are required: a URL without a key (or vice versa) can only make
// requests that fail, which is worse than the stub. Tests always run on the
// stub — unit tests must stay hermetic even with a real project configured.
const configured =
	SUPABASE_URL !== "" &&
	SUPABASE_ANON_KEY !== "" &&
	process.env.NODE_ENV !== "test";
const backend: ChallengeBackend = configured ? supabase : local;

// The verify sheet branches on this: "supabase" means sessions are real;
// "local" means the stub's theater (any input passes).
export const backendMode: "supabase" | "local" = configured
	? "supabase"
	: "local";

// Whether the identity flow includes the SMS OTP round-trip. False in
// anonymous auth mode (and on the stub): the verify sheet skips phone + code
// and goes straight to initials.
export const smsRequired = configured && SUPABASE_AUTH_MODE === "phone";

export const {
	getVerifiedSession,
	requestOtp,
	verifyOtp,
	setPlayerId,
	createChallengeRow,
	getChallengeRow,
	submitEntry,
	getLeaderboard,
} = backend;

// Backend-agnostic: "verified" means whatever the active implementation's
// getVerifiedSession says it means.
export const assertVerifiedSession = async (): Promise<void> => {
	const session = await getVerifiedSession();
	if (!session) {
		throw new Error("ChallengeNotVerified");
	}
};
